import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogIn } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import api, { getFileUrl } from '../../lib/api';
import { useMemberAuth } from '../../context/MemberAuthContext';

export const Navbar = ({ transparent = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [footer, setFooter] = useState(null);
  const [menuConfig, setMenuConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [customMenus, setCustomMenus] = useState([]);
  const [openCustomMenu, setOpenCustomMenu] = useState(null);
  const [membershipEnabled, setMembershipEnabled] = useState(false);
  const { isAuthenticated: memberLoggedIn } = useMemberAuth();
  const location = useLocation();
  const projTimeout = useRef(null);
  const svcTimeout = useRef(null);
  const customTimeout = useRef(null);

  useEffect(() => {
    api.get('/settings/footer').then(r => setFooter(r.data)).catch(() => {});
    api.get('/settings/menu').then(r => {
      const data = { ...r.data }; delete data.key;
      setMenuConfig(data);
    }).catch(() => {
      setMenuConfig({ kurumsal:true, projeler:true, ekatalog:true, blog:true, galeri:true, ekip:true, sss:true, iletisim:true, referanslar:true, hizmetler:true });
    });
    api.get('/services?active_only=true').then(r => setServices(r.data)).catch(() => {});
    api.get('/custom-pages/menus').then(r => setCustomMenus(r.data)).catch(() => {});
    api.get('/membership/status').then(r => setMembershipEnabled(!!r.data.membership_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); setProjectsOpen(false); setServicesOpen(false); }, [location]);

  const projEnter = () => { if (projTimeout.current) clearTimeout(projTimeout.current); setProjectsOpen(true); };
  const projLeave = () => { projTimeout.current = setTimeout(() => setProjectsOpen(false), 300); };
  const svcEnter = () => { if (svcTimeout.current) clearTimeout(svcTimeout.current); setServicesOpen(true); };
  const svcLeave = () => { svcTimeout.current = setTimeout(() => setServicesOpen(false), 300); };

  const isActive = (path) => location.pathname === path;
  const companyName = footer?.company_name || 'İnşaat';
  const logoPath = footer?.logo_path;
  // While menu config is still loading (null), hide optional menus to avoid a
  // flash of inactive menus that then disappear once the config arrives.
  const show = (key) => menuConfig ? menuConfig[key] !== false : false;

  const isTransparent = transparent && !scrolled;
  const navBg = isTransparent ? 'bg-transparent' : scrolled ? 'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70' : 'bg-background';
  const navBorder = isTransparent ? 'border-transparent' : 'border-border';
  const textColor = isTransparent ? 'text-white' : '';
  const mutedColor = isTransparent ? 'text-white/70 hover:text-white' : 'text-muted-foreground hover:text-foreground';
  const activeColor = 'text-[hsl(var(--gold))]';

  return (
    <header data-testid="navbar" className={`${transparent ? 'fixed' : 'sticky'} top-0 left-0 right-0 z-50 border-b ${navBorder} transition-all duration-300 ${navBg}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20 gap-2">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
            {footer === null ? (
              /* Loading skeleton — avoids flashing the default "İnşaat" text before real data loads */
              <>
                <div className="h-10 md:h-11 w-10 md:w-11 rounded-lg bg-muted-foreground/20 animate-pulse" />
                <div className="h-5 w-36 md:w-44 rounded bg-muted-foreground/20 animate-pulse" />
              </>
            ) : (
              <>
                {logoPath ? (
                  <img src={getFileUrl(logoPath)} alt={companyName} className="h-10 md:h-12 w-auto object-contain" />
                ) : (
                  <div className="w-8 h-8 bg-[hsl(var(--gold))] rounded-lg flex items-center justify-center">
                    <span className="text-[hsl(var(--accent-foreground))] font-heading font-bold text-sm">İ</span>
                  </div>
                )}
                <div className="flex flex-col">
                  <span className={`font-heading font-semibold text-lg tracking-tight leading-tight ${textColor}`}>{companyName}</span>
                  {footer?.slogan && <span className={`text-[10px] leading-tight hidden 2xl:block ${isTransparent ? 'text-white/60' : 'text-muted-foreground'}`}>{footer.slogan}</span>}
                </div>
              </>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-0.5" data-testid="desktop-nav">
            <NL to="/" active={isActive('/')} mc={mutedColor} ac={activeColor}>Anasayfa</NL>
            {show('kurumsal') && <NL to="/kurumsal" active={isActive('/kurumsal')} mc={mutedColor} ac={activeColor}>Kurumsal</NL>}
            
            {/* Projeler Dropdown */}
            {show('projeler') && (
              <div className="relative" onMouseEnter={projEnter} onMouseLeave={projLeave}>
                <button className={`flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${location.pathname.includes('/projeler') ? activeColor : mutedColor}`}
                  onClick={() => setProjectsOpen(prev => !prev)}>
                  Projeler <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {projectsOpen && (
                  <div className="absolute top-full left-0 pt-1 w-56 z-50">
                    <div className="bg-card border border-border rounded-xl shadow-[var(--shadow-elev-1)] py-2">
                      <DL to="/projeler/devam-eden">Devam Eden Projeler</DL>
                      <DL to="/projeler/tamamlanan">Tamamlanan Projeler</DL>
                      <DL to="/projeler/gelecek">Gelecek Projeler</DL>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Hizmetlerimiz Dropdown */}
            {show('hizmetler') && services.length > 0 && (
              <div className="relative" onMouseEnter={svcEnter} onMouseLeave={svcLeave}>
                <button className={`flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${location.pathname.includes('/hizmet') ? activeColor : mutedColor}`}
                  onClick={() => setServicesOpen(prev => !prev)}>
                  Hizmetlerimiz <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 pt-1 w-56 z-50">
                    <div className="bg-card border border-border rounded-xl shadow-[var(--shadow-elev-1)] py-2">
                      <DL to="/hizmetlerimiz">Tüm Hizmetler</DL>
                      {services.map(s => <DL key={s.id} to={`/hizmet/${s.slug}`}>{s.title}</DL>)}
                    </div>
                  </div>
                )}
              </div>
            )}
            {show('hizmetler') && services.length === 0 && (
              <NL to="/hizmetlerimiz" active={isActive('/hizmetlerimiz')} mc={mutedColor} ac={activeColor}>Hizmetlerimiz</NL>
            )}

            {show('ekatalog') && <NL to="/e-katalog" active={isActive('/e-katalog')} mc={mutedColor} ac={activeColor}>E-Katalog</NL>}
            {show('blog') && <NL to="/blog" active={isActive('/blog')} mc={mutedColor} ac={activeColor}>Haberler</NL>}
            {show('galeri') && <NL to="/galeri" active={isActive('/galeri')} mc={mutedColor} ac={activeColor}>Galeri</NL>}
            {show('ekip') && <NL to="/ekip" active={isActive('/ekip')} mc={mutedColor} ac={activeColor}>Ekip</NL>}
            {show('referanslar') && <NL to="/referanslar" active={isActive('/referanslar')} mc={mutedColor} ac={activeColor}>Referanslar</NL>}
            {show('sss') && <NL to="/sss" active={isActive('/sss')} mc={mutedColor} ac={activeColor}>SSS</NL>}

            {/* Custom Menus */}
            {customMenus.map(cm => {
              const children = cm.children || [];
              if (children.length === 0) {
                return <NL key={cm.id} to={`/sayfa/${cm.slug}`} active={isActive(`/sayfa/${cm.slug}`)} mc={mutedColor} ac={activeColor}>{cm.title}</NL>;
              }
              return (
                <div key={cm.id} className="relative"
                  onMouseEnter={() => { if (customTimeout.current) clearTimeout(customTimeout.current); setOpenCustomMenu(cm.id); }}
                  onMouseLeave={() => { customTimeout.current = setTimeout(() => setOpenCustomMenu(null), 300); }}>
                  <button className={`flex items-center gap-1 px-2 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${location.pathname.includes(`/sayfa/`) ? mutedColor : mutedColor}`}
                    onClick={() => setOpenCustomMenu(openCustomMenu === cm.id ? null : cm.id)}>
                    {cm.title} <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {openCustomMenu === cm.id && (
                    <div className="absolute top-full left-0 pt-1 w-56 z-50">
                      <div className="bg-card border border-border rounded-xl shadow-[var(--shadow-elev-1)] py-2">
                        {children.map(child => <DL key={child.id} to={`/sayfa/${child.slug}`}>{child.title}</DL>)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* İletişim - always last */}
            {show('iletisim') && <NL to="/iletisim" active={isActive('/iletisim')} mc={mutedColor} ac={activeColor}>İletişim</NL>}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {membershipEnabled && (
              memberLoggedIn ? (
                <Link to="/profilim" data-testid="navbar-profile-link"
                  className={`hidden xl:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${isTransparent ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-secondary'}`}>
                  <User className="w-4 h-4" /> Profilim
                </Link>
              ) : (
                <div className="hidden xl:flex items-center gap-1.5">
                  <Link to="/uye/giris" data-testid="navbar-login-link"
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${mutedColor}`}>
                    <LogIn className="w-4 h-4" /> Giriş Yap
                  </Link>
                  <Link to="/uye/kayit" data-testid="navbar-register-link"
                    className="inline-flex items-center px-3.5 py-2 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium whitespace-nowrap hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200">
                    Kayıt Ol
                  </Link>
                </div>
              )
            )}
            <ThemeToggle />
            <button className={`xl:hidden p-2 rounded-lg ${isTransparent ? 'hover:bg-white/10 text-white' : 'hover:bg-secondary'}`}
              onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <nav className="xl:hidden py-4 border-t border-border space-y-1 bg-background rounded-b-xl" data-testid="mobile-nav">
            <ML to="/" active={isActive('/')}>Anasayfa</ML>
            {show('kurumsal') && <ML to="/kurumsal" active={isActive('/kurumsal')}>Kurumsal</ML>}
            {show('projeler') && <>
              <ML to="/projeler/devam-eden">Devam Eden Projeler</ML>
              <ML to="/projeler/tamamlanan">Tamamlanan Projeler</ML>
              <ML to="/projeler/gelecek">Gelecek Projeler</ML>
            </>}
            {show('hizmetler') && <>
              <ML to="/hizmetlerimiz" active={isActive('/hizmetlerimiz')}>Hizmetlerimiz</ML>
              {services.map(s => <ML key={s.id} to={`/hizmet/${s.slug}`} active={isActive(`/hizmet/${s.slug}`)}>  — {s.title}</ML>)}
            </>}
            {show('ekatalog') && <ML to="/e-katalog" active={isActive('/e-katalog')}>E-Katalog</ML>}
            {show('blog') && <ML to="/blog" active={isActive('/blog')}>Haberler</ML>}
            {show('galeri') && <ML to="/galeri" active={isActive('/galeri')}>Galeri</ML>}
            {show('ekip') && <ML to="/ekip" active={isActive('/ekip')}>Ekip</ML>}
            {show('referanslar') && <ML to="/referanslar" active={isActive('/referanslar')}>Referanslar</ML>}
            {show('sss') && <ML to="/sss" active={isActive('/sss')}>SSS</ML>}
            {/* Custom Menus - Mobile */}
            {customMenus.map(cm => {
              const children = cm.children || [];
              if (children.length === 0) {
                return <ML key={cm.id} to={`/sayfa/${cm.slug}`} active={isActive(`/sayfa/${cm.slug}`)}>{cm.title}</ML>;
              }
              return (
                <React.Fragment key={cm.id}>
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{cm.title}</div>
                  {children.map(child => <ML key={child.id} to={`/sayfa/${child.slug}`} active={isActive(`/sayfa/${child.slug}`)}>  {child.title}</ML>)}
                </React.Fragment>
              );
            })}
            {/* İletişim - always last */}
            {show('iletisim') && <ML to="/iletisim" active={isActive('/iletisim')}>İletişim</ML>}
            {membershipEnabled && (
              <div className="pt-2 mt-2 border-t border-border">
                {memberLoggedIn ? (
                  <ML to="/profilim" active={isActive('/profilim')}>Profilim</ML>
                ) : (
                  <>
                    <ML to="/uye/giris" active={isActive('/uye/giris')}>Giriş Yap</ML>
                    <ML to="/uye/kayit" active={isActive('/uye/kayit')}>Kayıt Ol</ML>
                  </>
                )}
              </div>
            )}
          </nav>
        )}
      </div>
    </header>
  );
};

const NL = ({ to, active, children, mc, ac }) => (
  <Link to={to} className={`px-2 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${active ? ac : mc}`}>{children}</Link>
);
const DL = ({ to, children }) => (
  <Link to={to} className="block px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors duration-200">{children}</Link>
);
const ML = ({ to, active, children }) => (
  <Link to={to} className={`block px-4 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${active ? 'text-[hsl(var(--gold))] bg-secondary' : 'text-foreground hover:bg-secondary'}`}>{children}</Link>
);
